import DS from 'ember-data';
import Model from '@ember-data/model';
import attr from 'ember-data/attr';
import { empty, equal } from '@ember/object/computed';
import { computed } from '@ember/object';
import { belongsTo, hasMany } from 'ember-data/relationships';
import { modelApiAction } from 'cio/utils/model-api-actions';
import { SECONDS_IN_DAY } from 'cio/utils/constants';
import notEqual from 'cio/utils/not-equal';
import { inject as service } from '@ember/service';
import MomentService from 'cio/services/moment';
import moment from 'moment-timezone';

import UserModel from 'cio/models/user';
import PlanModel from 'cio/models/plan';
import AccountUsageModel from 'cio/models/account-usage';
import FeaturesService from 'ember-feature-flags/services/features';
import ApiService from 'cio/services/api';
import ApiErrorsService from 'cio/services/api-errors';
import EnvironmentModel from 'cio/models/environment';
import { SignResponse } from 'cio/utils/uploads';

interface UpdatePlanOptions {
  plan: PlanModel;
  annual: boolean;
}

interface SubscribeCardOptions {
  setupIntent: unknown;
  planId?: string;
  couponCode?: string;
  annual?: boolean;
}

export const SSOSettingOff = 'off';
export const SSOSettingRequired = 'required';

export const DEFAULT_SUPPORT_TEAM_ACCESS_PERIOD_IN_DAYS = 7;
export const MIN_SUPPORT_TEAM_ACCESS_PERIOD_IN_DAYS = 3;
export const MAX_SUPPORT_TEAM_ACCESS_PERIOD_IN_DAYS = 180;

export const DEFAULT_SUCCESS_TEAM_ACCESS_PERIOD_IN_DAYS = 90;
export const MIN_SUCCESS_TEAM_ACCESS_PERIOD_IN_DAYS = 3;
export const MAX_SUCCESS_TEAM_ACCESS_PERIOD_IN_DAYS = 365;

export type SSOSettingType = typeof SSOSettingOff | typeof SSOSettingRequired;

export enum AccountState {
  Free = 'free',
  Current = 'current',
  Canceled = 'canceled',
  Suspended = 'suspended',
  Overdue = 'overdue',
}

export enum PlanFeatures {
  WarehouseSync = 'warehouseSync',
  Collections = 'collections',
  Workspaces = 'workspaces',
  ObjectTypes = 'objectTypes',
  Messaging = 'messaging',
  MaxObjectJourneys = 'max_object_journeys',
}

export enum VerificationState {
  Pending = 'pending',
  Approved = 'approved',
  Rejected = 'rejected',
  Unsubmitted = 'unsubmitted',
}

export type PlanFeatureLimit = {
  limit: number;
  days: number;
  start_at: Date;
};

export enum PlanLimits {
  workspaces = 'workspaces',
  objectTypes = 'objectTypes',
}

export enum DataCenter {
  US = 'us',
  EU = 'eu',
}

export default class AccountModel extends Model {
  @service features!: FeaturesService;
  @service store!: DS.Store;
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;
  @service moment!: MomentService;

  @attr('string') accountState!: AccountState;
  @attr('string') email!: string;
  @attr('string') invoiceEmail!: string;
  @attr('string') legalEmail!: string;
  @attr('string') technicalEmail!: string;
  @attr('string') addressLine1!: string;
  @attr('string') addressLine2!: string;
  @attr('string') ssoSetting!: SSOSettingType;
  @attr('string') city!: string;
  @attr('string') state!: string;
  @attr('string') zipCode!: string;
  @attr('string') country!: string;
  @attr('string') name!: string;
  @attr('string') firstName!: string;
  @attr('string') lastName!: string;
  @attr('string') logoUrl!: string;
  @attr('string') password!: string;
  @attr('string') accessToken!: string;
  @attr('number') planPrice!: number;
  @attr('number') planPeriodStart!: number;
  @attr('number') planPeriodEnd!: number;
  @attr('boolean') planIncludeChurn!: boolean;
  @attr('number') createdAt!: number;
  @attr('string') systemNotification!: string;
  @attr('boolean') admin!: boolean;
  @attr('string') verificationState!: VerificationState;
  @attr('boolean') confirmed!: boolean;
  @attr('number') suspendTrialAt!: number;
  @attr('number') cancelTrialAt!: number;
  @attr('number') suspendIncompleteSubscriptionAt!: number;
  @attr('number') nextInvoiceDate!: number;
  @attr('boolean') premium!: boolean;
  @attr('boolean') premiumTrial!: boolean;
  @attr('boolean') twoFactorAuthenticationRequired!: boolean;
  @attr('number') deleteAt?: number;
  @attr('boolean') canReactivate?: number;
  @attr('boolean') isMasqueraded!: boolean;
  @attr('string') dataCenter!: DataCenter;
  @attr('boolean') enableTroubleshootingCookies!: boolean;
  @attr('boolean') readOnly!: boolean;
  @attr('string') supportTier!: string;
  @attr('array') productActivations!: string[];

  // In Ember Data 4.12 this somehow gets set to UserModel (sic!)
  // This happens only when masquerading and  "accessGrantedBy" - async relationship
  // on user - is non-empty.
  //
  // TODO: maybe investigate more (a crazy-looking bug, but it's real!)
  // @belongsTo('account-usage') usage!: AccountUsageModel;
  //
  // A workaround: a single "account-usage" is always loaded, so let's do a store peek and call it a day:
  get usage() {
    return this.store.peekAll('account-usage').slice()[0] as AccountUsageModel;
  }

  @attr() planFlags!: unknown; // JSON object
  @attr() featureFlags!: Record<string, boolean>; // JSON object
  @attr() gatedFeatures!: Record<string, PlanFeatureLimit | Record<string, never>> | undefined;

  @belongsTo('user', { async: false, inverse: null }) currentUser!: UserModel;
  @hasMany('environment', { async: false, inverse: 'account' }) environments!: DS.ManyArray<EnvironmentModel>;

  @attr('number') accessGrantedUntil!: number;
  @belongsTo('user', { async: true, inverse: null }) accessGrantedBy!: UserModel;
  @attr('number') accessGrantedOn!: number;

  @computed('accessGrantedUntil', 'moment.currentTimestamp')
  get supportTeamAccessEnabled(): boolean {
    return this.moment.currentTimestamp < this.accessGrantedUntil;
  }

  @attr('number') csAccessGrantedUntil!: number; // "-1" means "indefinitely"
  @belongsTo('user', { async: true, inverse: null }) csAccessGrantedBy!: UserModel;
  @attr('number') csAccessGrantedOn!: number;

  @computed('csAccessGrantedUntil', 'moment.currentTimestamp')
  get successTeamAccessEnabled(): boolean {
    return this.moment.currentTimestamp < this.csAccessGrantedUntil || this.csAccessGrantedUntil === -1;
  }

  @attr('boolean') hasCsm!: boolean;

  @computed('logoUrl')
  get logoUrlWithFallback() {
    return this.logoUrl || new URL('cio/assets/images/default-account-logo.svg', import.meta.url).toString();
  }

  // overdue accounts will be cancelled on day preceding planned issue date of next invoice:
  @computed('overdue', 'nextInvoiceDate')
  get scheduledCancellationDate() {
    if (!this.overdue || !this.nextInvoiceDate) {
      return;
    }

    return this.nextInvoiceDate - SECONDS_IN_DAY;
  }

  @empty('verificationState') verificationNeeded!: boolean;
  @equal('verificationState', VerificationState.Pending) verificationPending!: boolean;
  @equal('verificationState', VerificationState.Approved) verificationApproved!: boolean;
  @equal('verificationState', VerificationState.Rejected) verificationRejected!: boolean;

  @equal('accountState', AccountState.Suspended) suspended!: boolean;
  @equal('accountState', AccountState.Canceled) canceled!: boolean;
  @equal('accountState', AccountState.Overdue) overdue!: boolean;

  @equal('ssoSetting', SSOSettingRequired) ssoRequiredEnabled!: boolean;
  @notEqual('ssoSetting', SSOSettingOff) ssoEnabled!: boolean;

  @equal('dataCenter', DataCenter.EU) isDataCenterEU!: boolean;
  @equal('dataCenter', DataCenter.US) isDataCenterUS!: boolean;

  signLogo = modelApiAction<{ name: string; type: string; size: string }, SignResponse & Record<string, any>>({
    path: 'sign_logo',
    type: 'POST',
  });
  cancel = modelApiAction({ path: 'cancel', type: 'POST' });

  async verify(data?: Record<string, any>) {
    return this.api.post('verify', { dataCenter: DataCenter.US, data, pathScope: 'account' });
  }

  async setSupportAccessConsent(days: number) {
    let now = moment().unix();
    let timestamp = now + days * 24 * 60 * 60;
    if (days <= 0) {
      timestamp = 0;
    }
    try {
      await this.api.put('set_support_access', {
        data: { Account: { access_granted_until: timestamp } },
        pathScope: 'account',
      });
      this.set('accessGrantedOn', now);
      this.set('accessGrantedBy', this.currentUser);
      this.set('accessGrantedUntil', timestamp);
      if (this.accessGrantedBy.get('id') !== this.currentUser.get('id')) {
        this.accessGrantedBy.reload();
      }
    } catch (e) {
      this.rollbackAttributes();
      this.apiErrors.report(e);
      throw e;
    }
  }

  async setSuccessAccessConsent(options: { days: number; infinite?: boolean }) {
    let { days, infinite } = options;
    if (days == null && infinite == null) {
      throw new Error('Either days or infinite must be specified');
    }

    let timestamp = 0;
    let now = moment().unix();
    if (infinite) {
      timestamp = -1;
    } else {
      timestamp = now + days * 24 * 60 * 60;
      if (days <= 0) {
        timestamp = 0;
      }
    }

    try {
      await this.api.put('set_success_access', {
        data: { Account: { cs_access_granted_until: timestamp } },
        pathScope: 'account',
      });
      this.set('csAccessGrantedOn', now);
      this.set('csAccessGrantedBy', this.currentUser);
      this.set('csAccessGrantedUntil', timestamp);
      if (this.csAccessGrantedBy.get('id') !== this.currentUser.get('id')) {
        this.csAccessGrantedBy.reload();
      }
    } catch (e) {
      this.rollbackAttributes();
      this.apiErrors.report(e);
      throw e;
    }
  }

  _updatePlan = modelApiAction({ path: 'plan', type: 'POST' });
  updatePlan({ plan, annual }: UpdatePlanOptions) {
    return this._updatePlan({ plan_id: Number(plan.get('id')), annual });
  }

  _subscribe = modelApiAction({ path: 'subscribe', type: 'POST' });
  subscribe({ setupIntent, planId, couponCode = '', annual = false }: SubscribeCardOptions) {
    return this._subscribe({
      setup_intent: setupIntent,
      plan_id: Number(planId),

      coupon_code: couponCode,
      annual,
    });
  }

  _updateCard = modelApiAction({ path: 'card', type: 'POST' });
  updateCard(this: AccountModel, { setupIntent }: SubscribeCardOptions) {
    return this._updateCard({ setup_intent: setupIntent });
  }

  enableTwoFactorAuthRequired = modelApiAction({ path: '2fa_required/enable', type: 'POST' });
  disableTwoFactorAuthRequired = modelApiAction({ path: '2fa_required/disable', type: 'POST' });

  reactivateAsSuspended = modelApiAction({
    path: 'reactivate_as_suspended',
    type: 'POST',
  });

  retryPayment = modelApiAction({ path: 'retry_payment', type: 'POST' });

  toggleFeatureFlag = modelApiAction({
    path: 'toggle_feature_flag',
    type: 'PUT',
    before(payload: { flag: string; enabled: boolean }) {
      return payload;
    },
    after(this: AccountModel, response) {
      this.store.pushPayload(response);
      this.updateFeatureFlags();
    },
  });

  private updateFeatureFlags = () => {
    Object.entries(this.featureFlags).forEach(([flag, isEnabled]) => {
      let currentIsEnabled = this.features.isEnabled(flag);

      if (currentIsEnabled === isEnabled) {
        return;
      }

      if (isEnabled) {
        this.features.enable(flag);
      } else {
        this.features.disable(flag);
      }
    });
  };
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    account: AccountModel;
  }
}
